import React from "react";
import {
  CCol,
  CButton,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { useReceiptFormContext } from "../context/ReceiptFormContext";
import { FieldErrors } from "../../form/FieldErrors";
import { extractArrayErrors, getFieldErrors } from "../../../models/errors";
import { CURRENCY_CHOICES } from "../../../currency/available-currencies";
import {
  RECEIPT_PAYMENT_METHOD_CHOICES,
  RECEIPT_PAYMENT_METHOD_CHECK,
} from "../../../models/receipt";
import CurrencyField from "../../currencies/CurrencyField";
import ReceiptPaymentMethodForm from "../ReceiptPaymentMethodForm";
import ReceiptFormSection from "./ReceiptFormSection";

const FormLabel: React.FC<{ required?: boolean; htmlFor?: string; children: React.ReactNode }> = ({ required, htmlFor, children }) => (
  <CLabel htmlFor={htmlFor} className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

const ReceiptFormPaymentSection: React.FC = () => {
  const {
    receiptRef,
    currency,
    paymentMethod,
    totalAmount,
    errors,
    onCurrencyChange,
    onPaymentMethodChange,
    onCheckNumberChange,
    onCheckBankChange,
    onTotalAmountChange,
    paymentMethods,
    onPaymentMethodRowChange,
    onPaymentMethodRowDelete,
    onAddPaymentMethodClick,
    readonly,
  } = useReceiptFormContext();
  const paymentErrors = extractArrayErrors("paymentMethods", errors);

  return (
    <ReceiptFormSection id="receipt-payment" icon="fa-money" label="Pago">
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required htmlFor="receipt-currency">Moneda:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              id="receipt-currency"
              value={currency}
              disabled={readonly}
              onChange={onCurrencyChange}
            >
              <option value={""} disabled>
                -----
              </option>
              {Array.from(CURRENCY_CHOICES.entries()).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("currency", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required htmlFor="receipt-payment-method">Forma de Pago:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              id="receipt-payment-method"
              value={paymentMethod}
              disabled={readonly}
              onChange={onPaymentMethodChange}
            >
              <option value={""}>-----</option>
              {Array.from(RECEIPT_PAYMENT_METHOD_CHOICES.entries()).map(
                (entry) => {
                  return (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  );
                },
              )}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("paymentMethod", errors) as string[]}
            />
          </CCol>
          {paymentMethod === RECEIPT_PAYMENT_METHOD_CHECK ? (
            <>
              <CCol md={2}>
                <FormLabel htmlFor="receipt-check-number">Nº Cheque (opcional):</FormLabel>
              </CCol>
              <CCol md={2}>
                <CInput
                  type="text"
                  id="receipt-check-number"
                  maxLength={255}
                  disabled={readonly}
                  defaultValue={
                    receiptRef.current?.checkNumber ?? ""
                  }
                  onChange={onCheckNumberChange}
                  placeholder="Nro"
                />
                <FieldErrors
                  errors={getFieldErrors("checkNumber", errors) as string[]}
                />
              </CCol>
              <CCol md={2}>
                <FormLabel htmlFor="receipt-check-bank">Banco Cheque (opcional):</FormLabel>
              </CCol>
              <CCol md={2}>
                <CInput
                  type="text"
                  id="receipt-check-bank"
                  maxLength={255}
                  disabled={readonly}
                  defaultValue={
                    receiptRef.current?.checkBank ?? ""
                  }
                  onChange={onCheckBankChange}
                  placeholder="Banco"
                />
                <FieldErrors
                  errors={getFieldErrors("checkBank", errors) as string[]}
                />
              </CCol>
            </>
          ) : (
            <></>
          )}
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Total:</FormLabel>
          </CCol>
          <CCol md={2}>
            <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "var(--theme-accent)" }}>
              <CurrencyField
                currency={currency}
                placeholder="Total"
                limit={1000000000000}
                disabled={readonly}
                onChange={onTotalAmountChange}
                value={totalAmount}
              />
            </div>
            <FieldErrors
              errors={getFieldErrors("amount", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <div className="mb-2 mt-3">
        <FormLabel>Desglose de pagos</FormLabel>
      </div>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Opcional. Agregue pagos si utiliza varias formas de pago. Sin pagos agregados, se usará la forma de pago seleccionada.
      </p>
      <FieldErrors errors={getFieldErrors("paymentMethods", errors) as string[]} />
      {paymentMethods.map((payment, index) => (
        <ReceiptPaymentMethodForm
          key={index} payment={payment} index={index} errors={paymentErrors[index] ?? {}} readonly={readonly}
          onChange={(updated) => onPaymentMethodRowChange(index, updated)}
          onDelete={() => onPaymentMethodRowDelete(index)}
        />
      ))}
      <div className="d-flex justify-content-end mb-3">
        <CButton type="button" color="primary" variant="outline" disabled={readonly} onClick={onAddPaymentMethodClick}>
          <i className="fa fa-plus mr-2" aria-hidden="true" /> Agregar forma de pago
        </CButton>
      </div>
    </ReceiptFormSection>
  );
};

export default ReceiptFormPaymentSection;
